import { FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { FormGroupType } from '../../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../../core/models/resources/action-code';
import { ApplicationStateService } from '../../../../../shared/services/application-state/application-state.service';
import { ScheduleDFormModel } from '../../../models/schedule-d-form-model';
import { ScheduleDFormService } from '../../../services/schedule-d-form/schedule-d-form.service';
import { ScheduleDLocationType } from '../../../types/schedule-d-location-type';
import { ScheduleDStepControlsMap } from '../../../types/schedule-d-step-controls-map';

export abstract class ScheduleDStepBaseService<
    T extends string | number | symbol,
> {
    protected stepControlMap!: ScheduleDStepControlsMap<T>;

    constructor(
        protected readonly formService: ScheduleDFormService,
        protected readonly applicationStateService: ApplicationStateService,
    ) {}

    abstract getLocationType(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): T | null;

    abstract isStepValid(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): Observable<boolean>;

    updateFormControls(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        const locationIsSavedToApplication = form.controls.id.value !== null;
        const type = this.getLocationType(form);

        if (!type) {
            return;
        }

        this.formService.removeRequiredValidatorsNotInType(
            form,
            type,
            this.stepControlMap,
        );

        this.stepControlMap[type].forEach((controlKey) => {
            const control = form.get(controlKey);
            if (!control) {
                return;
            }

            // When this is an existing schedule D form, we want to highlight controls that are not valid
            // so the user can see what needs to be fixed. Whereas, when this is a new schedule D form,
            // we don't want to highlight controls that are not valid until the user has interacted with them.
            if (
                locationIsSavedToApplication ||
                form.controls.actionCode.value !== ActionCode.Add
            ) {
                control.markAsTouched();
            }

            control.addValidators(
                this.formService.controlValidators[controlKey],
            );

            const controlValidatorsSpecificToLocationType =
                this.formService
                    .requiredControlValidatorsSpecificToLocationType[
                    type as ScheduleDLocationType
                ];
            if (
                controlValidatorsSpecificToLocationType &&
                controlValidatorsSpecificToLocationType!.includes(controlKey)
            ) {
                control.addValidators(Validators.required);
            }

            // Set some default values for controls if it is not an existing license application.
            if (!this.applicationStateService.IsExistingLicenseApplication()) {
                if (
                    this.formService.defaultControlValuesMap[controlKey] !==
                        undefined &&
                    !control.dirty
                ) {
                    control.setValue(
                        this.formService.defaultControlValuesMap[controlKey],
                        {
                            emitEvent: false,
                        },
                    );
                }
            }

            control.updateValueAndValidity({ emitEvent: false });
        });
    }
}
